import { DatePipe } from '@angular/common';
import { DraftListAttributes, DraftListTableColumn, DraftObject } from '../../../initiate-and-release/Models/Drafts/DraftResponse';
import { ApiFrequency, DisplayFrequency, TransactionDirection } from '../../../shared/types/Transaction';
import { amountFormatter } from '../../../shared/utilities/AmountFormatter';
import { FilterDate } from '../../models';
import { formType } from '../../../shared/utilities/Constants';
import { DateTime } from 'luxon';
import { dateFormatFlexible } from '../../../shared/utilities/regex-patterns';

export const sharedColNames = {
  transactionType: 'Type',
  toFromAccount: 'To/From',
  actions: 'Actions',
  status: 'Status',
  accountNumber: 'Account number',
  accountName: 'Account name',
  registrationType: 'Registration type',
  direction: 'Direction',
  amount: 'Amount'
};

export const amountColumnTemplate = `<div class="ag-cell-label-container" role="presentation">
<div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
  <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
  <span data-ref="eSortAsc"></span>
  <span data-ref="eSortDesc"></span>
  <span data-ref="eSortNone"></span>
  <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
  <span data-ref="eFilter" class="sch-search"></span>
</div>
</div>`

export const defaultColumnTemplate = `<div class="ag-cell-label-container" role="presentation">
<div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
  <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
  <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
  <span data-ref="eSortAsc" ></span>
  <span data-ref="eSortDesc"></span>
  <span data-ref="eSortNone"></span>
  <span data-ref="eFilter" class="sch-search"></span>
</div>
</div>`

export function mapTransactionType(transactionType: string, direction?: TransactionDirection): string {
  if (transactionType.includes('Set-Up')) {
    transactionType = transactionType.replace('Set-Up', '');
  }
  if (transactionType.includes('Journal-')) {
    return transactionType.replace('Journal-', 'Internal transfer - ');
  }
  else if (transactionType.includes('Journal')) {
    return transactionType.replace('Journal', 'Internal transfer');
  }
  else if (transactionType.includes('MoneyLink')) {
    return transactionType.replace('MoneyLink', 'ACH');
  }
  else if (transactionType.includes('Moneylink')) {
    return transactionType.replace('Moneylink', 'ACH');
  }
  else return transactionType;
}

export function mapTweRecurringTransactionType(mediaType: string): string {
  switch (mediaType) {
    case 'C':
      return 'Check';
    case 'J':
      return 'Journal';
    case 'W':
      return 'Wire';
    case 'A':
      return 'ACH'
  }
  return '';
}

// DATE COMPARATOR FOR SORTING
export function dateComparator(date1: string, date2: string): number {
  const isNA = (value: string) => value?.toLowerCase() === 'n/a';

  const isDate1NA = isNA(date1);
  const isDate2NA = isNA(date2);
  
  if (isDate1NA && isDate2NA) return 0;
  if (isDate1NA) return 1;
  if (isDate2NA) return -1;
  
  const date1Number = _monthToNum(date1);
  const date2Number = _monthToNum(date2);
  
  if (date1Number === null && date2Number === null) return 0;
  if (date1Number === null) return 1;
  if (date2Number === null) return -1;
  
  return date1Number - date2Number;
}

// HELPER FOR DATE COMPARISON
export function _monthToNum(date: string): number | null {
  if (date === undefined || date === null || date.length !== 10 || date.toLowerCase() === 'n/a') {
    return null;
  }

  var yearNumber = +date.substring(6, 10);
  var dayNumber = +date.substring(3, 5);
  var monthNumber = +date.substring(0, 2);

  var result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
  // 08/29/2004 => 20040829
  return result;
}

export function dateComparatorForFilter(filterLocalDateAtMidnight: Date, cellValue: string): number {
  const dateAsString = cellValue;
  const dateMatch = dateAsString?.match(dateFormatFlexible);

  if (dateAsString == null || 
      dateAsString.toLowerCase() === 'n/a' || 
      !dateMatch) {
    return -1;
  }

    // We create a Date object for comparison against the filter date
  const dateParts = dateMatch[1].split('/');
  const day = Number(dateParts[1]);
  const month = Number(dateParts[0]) - 1;
  const year = Number(dateParts[2]);
  const cellDate = new Date(year, month, day);

  if (isNaN(day) || isNaN(month) || isNaN(year) || isNaN(cellDate.getTime())) {
    return -1;
  }

  if (cellDate < filterLocalDateAtMidnight) {
    return -1;
  } else if (cellDate > filterLocalDateAtMidnight) {
    return 1;
  }
  return 0;
}

/**
   * @description A function to get the date of a specified number of days from today.
   * @param interval The number of days from today to get the date of. Negative for past dates, positive for future dates, zero for today.
   * @returns A FilterDate of the date that is calculated from 'interval' number of days from today.
*/
export function getFilterDate(interval: number = 0): FilterDate {
  const today = new Date().getDate();
  const calculatedDay = new Date().setDate(today + interval);
  const date = new Date(calculatedDay);
  const onlyDate = new Date(new Date(calculatedDay).setHours(0, 0, 0, 0));
  return { onlyDate: onlyDate, fullDate: date, ...getDayMonthYear(date) };
}

export function getDayMonthYear(date: Date) {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const currentYear = date.getFullYear();

  return {
    day: day,
    month: month,
    year: currentYear,
  };
}

/**
 * Takes an unformatted date in either MM-DD-YYYY or MM/DD/YYYY format and converts
 * to ISO format (YYYY-MM-DD) to work with Date constructor for multiple browsers
 * @param unformattedDate
 * @returns date in ISO format (YYYY-MM-DD)
 */
export function getProcessDateInIsoFormat(unformattedDate: string): string {
  if(unformattedDate?.length !== 10) {
    return unformattedDate;
  }
  const dateArray = unformattedDate.includes('/') ? unformattedDate.split('/') : unformattedDate.split('-');
  if(dateArray.length === 3) {
    return dateArray[2] + '-' + dateArray[0] + '-' + dateArray[1] + 'T00:00:00';
  }
  return unformattedDate;
}

export function getDisplayFrequency(apiFrequency: ApiFrequency, mediaTypeCd: string, isRecurring: boolean = true): DisplayFrequency {
  switch (apiFrequency) {
    case 'ONETIME':
    case 'ONRQST':
    case 'ONREQST':
      return 'One-time';
    case 'MONTHLY':
      return 'Monthly';
    case 'ANNUAL':
    case 'ANNUALY':
      return 'Annual';
    case 'BIMTHLY':
      return 'Every 2 months';
    case 'LASTBIZ':
      return 'Last biz day';
    case 'QURTRLY':
      return 'Quarterly';
    case 'SEMIANN':
      return 'Semi-annual';
    case 'SEMIMTH':
      return '2 times/month';
    case 'EOWEEK':
      return 'Every other week';
    case 'WKLYMON':
      return 'Weekly - Mon';
    case 'WKLYTUE':
      return 'Weekly - Tues';
    case 'WKLYWED':
      return 'Weekly - Wed';
    case 'WKLYTHU':
      return 'Weekly - Thurs';
    case 'WKLYFRI':
      return 'Weekly - Fri';
    case 'WEEKLY':
      return 'Weekly';
    case 'ACCRUED':
      return 'As accrued';
    default:
      if (mediaTypeCd === 'A' && !isRecurring) {
        return 'One-time'
      }
      else {
        return 'N/A';
      }
  }

}

export function currencyComparator(valueA: string | number, valueB: string | number) {
  const isNA = (value: string | number) => typeof value === 'string' && value?.toLowerCase() === 'n/a';
  const toNumber = (value: string | number) => typeof value === 'string' ? +value.replace(/[^\w\s^.]/gi, "") : +value;
  
  const aIsNA = isNA(valueA);
  const bIsNA = isNA(valueB);
  
  if (aIsNA && bIsNA) return 0;
  if (aIsNA) return -1;
  if (bIsNA) return 1;
  
  const numA = toNumber(valueA);
  const numB = toNumber(valueB);
  const aValid = !isNaN(numA);
  const bValid = !isNaN(numB);
  
  if (!aValid && !bValid) return 0;
  if (!aValid) return -1;
  if (!bValid) return 1;
  
  return numA - numB;
}

export function mapInitiatedDraftsToTableColumn(draft: DraftListAttributes): DraftListTableColumn {
  let datePipe = new DatePipe('en-US');
  return {
    'Master account': draft.masterAccount?.slice(0, 4) + '-' + draft.masterAccount?.slice(4),
    'Sub Account': draft.subAccount?.slice(0, 4) + '-' + draft.subAccount?.slice(4),
    'Process date':
      datePipe.transform(draft.transactionProcessDate, 'MM/dd/yyyy', 'UTC') ??
      'N/A',
    'Initiation date':
      datePipe.transform(draft.createDate, 'MM/dd/yyyy') ?? 'N/A',
    'Initiator ID': !draft.createdByLoginId ? draft.draftObject?.assignToUserId ?? draft.draftObject?.AssignToUserId ?? 'N/A' : draft.createdByLoginId,
    Type: mapParty(draft.draftObject?.isThirdParty ?? false, draft.draftObject?.thirdPartyIndValue ?? '', draft.draftObject.payeeType ?? '') + ' - ' + mapTransactionTypeDraft(draft),
    'Incoming/Outgoing': draft?.fundsDirection === "I" ? "Incoming" : "Outgoing",
    'To/From': getToFrom(draft),
    Amount: `${amountFormatter.format(draft.transactionAmount)}`,
    Status: draft.transactionStatus === 'deleted'
      ? 'Cancelled'
      : draft.transactionStatus.charAt(0).toUpperCase() + draft.transactionStatus?.slice(1),
    DraftId: draft.draftId,
    isSloa: draft.draftObject?.formType?.toLowerCase().includes('sloa')
  };
}

export function mapParty(isThirdParty: boolean, thirdPartyIndicator: string, payeeType: string): string {
  if (isThirdParty || thirdPartyIndicator === 'Y' || payeeType === 'payeeThirdParty' || payeeType === 'payeeEscrow') {
    return '3rd Party';
  }
  else {
    return '1st Party';
  }
}

function getToFrom(draft: DraftListAttributes): string {
  let transactionType = draft.transactionType;
  switch (transactionType) {
    case 'check': {
      let payeename1 =
        draft.draftObject.payeeDetails?.payeeName1 != null &&
          draft.draftObject.payeeDetails?.payeeName1 != ''
          ? draft.draftObject.payeeDetails?.payeeName1
          : draft.draftObject.payeeLine1 ?? '';
      let payeename2 =
        draft.draftObject.payeeDetails?.payeeName2 != null &&
          draft.draftObject.payeeDetails?.payeeName2 != ''
          ? draft.draftObject.payeeDetails?.payeeName2
          : draft.draftObject.payeeLine2 ?? '';
      let payeename3 =
        draft.draftObject.payeeDetails?.payeeName3 != null &&
          draft.draftObject.payeeDetails?.payeeName3 != ''
          ? draft.draftObject.payeeDetails?.payeeName3
          : draft.draftObject.payeeLine3 ?? '';

      return payeename1 + payeename2 + payeename3;
    }
    case 'wire': {
      return draft?.draftObject?.beneAccount ?? '';
    }
    case 'journal': {
      return draft?.draftObject?.toAccountId?.toString() ?? '';
    }
    case 'moneylink': {
      return draft?.draftObject?.abaNumber ?? '';
    }
    default:
      return '';
  }
}

function mapTransactionTypeDraft(draft: DraftListAttributes): string {
  if (draft.transactionType.includes('wire')) {
    return mapWireFormType(draft.draftObject.formType, draft);
  }
  else if (draft.transactionType.includes('journal')) {
    return mapTransactionType(draft.draftObject.formType);
  }
  else if (draft.transactionType.includes('moneylink')) {
    // Legacy UI created draft doesn't contain frequency field 
    // Adding below null check to make I&R flow cross UI compatible
    let frequency = draft.draftObject.frequency ? (draft.draftObject.frequency?.toString().toLowerCase() === 'onetime' ? 'One Time' : 'Recurring') : 'One Time';
    return frequency + ' ' + draft.transactionType.replace('moneylink', 'ACH');
  }
  else if (draft.transactionType.includes('check')) {
    return mapCheckFormType(draft, draft.draftObject, draft.draftObject.formType);
  }
  else return draft.transactionType;
}

function mapWireFormType(formTypeWire: string, draft: DraftListAttributes) {
  if (draft.draftObject.bSLOATxn && draft.draftObject.isSLOA) {
    return formType.formTypeReturnWireBSLOASetup;
  }
  switch (formTypeWire) {
    case formType.formTypeWire:
      return formType.formTypeReturnOTWire;
    case formType.formTypeCreateSloaWire:
      return formType.formTypeReturnWireSloaSetup;
    case formType.formTypeEditWire:
      return formType.formTypeReturnOnReqWire;
    case formType.formTypeRequestWire:
      return formType.formTypeReturnWire;
    default:
      return '';
  }
}

function mapJournalFormType(draft: DraftListAttributes, draftObject: DraftObject, formTypeJournal: string) {
  if (draft.draftObject.bsloaTransaction && draft.draftObject.isCreateSloa) {
    return formType.formTypeReturnJournalBSLOASetup;
  }
  switch (formTypeJournal) {
    case formType.formTypeJournal:
      return mapOneTimeJournal(draft, draftObject);
    case formType.formTypeRecJournal:
      return formType.formTypeReturnRecJournal;
    case formType.formTypeCreateSloaJournal:
      return formType.formTypeReturnJournalSloaSetup;
    default:
      return '';
  }
}

function mapOneTimeJournal(draft: DraftListAttributes, draftObject: DraftObject) {
  if (draft.draftObject.isSloaPopupSelected) {
      return formType.formTypeReturnOnReqJournal;
  }
  return formType.formTypeReturnOTJournal;
}

function mapCheckFormType(draft: DraftListAttributes, draftObject: DraftObject, formTypeCheck: string) {
  switch (formTypeCheck) {
    case formType.formTypeCheck:
      return mapOneTimeCheck(draft, draftObject);
    case formType.formTypeRecCheck:
      return formType.formTypeReturnRecCheck;
    case formType.formTypeCheckSloa:
      return draftObject.bSLOATxn ? formType.formTypeReturnCheckBSLOASetup : formType.formTypeReturnCheckSloaSetup;
    case formType.formTypeSloaCheck:
      return formType.formTypeReturnCheckSloaSetup;
    default:
      return '';
  }
}

function mapOneTimeCheck(draft: DraftListAttributes, draftObject: DraftObject) {
    if (draft.draftObject.sloaPopupSelected)
    {
      return formType.formTypeReturnOnReqCheck;
    }
    return formType.formTypeReturnOTCheck;
}
